import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Printer, Camera, ScanText, Tag, Sparkles, AlertOctagon, ArrowLeft } from "lucide-react";

// THE ONE-PAGE ASK-AI CREW GUIDE (his instruction, 2026-10-14).
//
// *"Write a plain one-page guide my crew can read — what the photos are actually for, how to shoot
// a part so the AI can identify it AND read the numbers, and what a bolt-on component is."*
//
// Written for the floor, not for a developer: short lines, no jargon, nothing about tokens, models
// or confidence scores. It prints on one page so a copy can live on the bench.
const Step = ({ n, children }) => (
  <div className="flex gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-[#1F1F1F] text-white text-xs font-bold grid place-items-center print:bg-slate-800">{n}</span>
    <div className="text-sm text-slate-700 leading-relaxed">{children}</div>
  </div>
);

const Block = ({ icon: Icon, title, tone = "slate", children, testid }) => (
  <section data-testid={testid} className={`rounded-xl border p-4 break-inside-avoid ${
    tone === "amber" ? "border-amber-200 bg-amber-50/60" : tone === "red" ? "border-red-200 bg-red-50/50" : "border-slate-200 bg-white"}`}>
    <h2 className="flex items-center gap-2 text-base font-bold text-slate-800 mb-2.5">
      <Icon className="w-4 h-4 text-[#E0781A]" />{title}
    </h2>
    <div className="space-y-2.5">{children}</div>
  </section>
);

export default function AskAiGuidePage() {
  return (
    <div data-testid="ask-ai-guide" className="max-w-3xl mx-auto space-y-4 pb-10">
      <div className="flex items-center justify-between gap-3 print:hidden">
        <Link to="/sort-queue" data-testid="guide-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="w-4 h-4" />Back to Identify
        </Link>
        <Button data-testid="guide-print" variant="outline" className="rounded-lg" onClick={() => window.print()}>
          <Printer className="w-4 h-4 mr-1.5" />Print this page
        </Button>
      </div>

      <header>
        <h1 className="font-display text-3xl font-bold text-slate-900">Ask AI — how to use it</h1>
        <p className="text-sm text-slate-500 mt-1">One page. Read it once, work it every day.</p>
      </header>

      <Block icon={Camera} title="What the photos are actually for" testid="guide-photos">
        <p className="text-sm text-slate-700">The AI does <b>two different jobs</b>, and they want two different photos.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Identify this part</div>
            <p className="text-sm text-slate-700">It looks at the <b>shape of the whole part</b> to say what it is — name, type, category. It needs to see the part stood back from, all of it in frame, in decent light.</p>
          </div>
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Read numbers off the part</div>
            <p className="text-sm text-slate-700">It reads the <b>characters stamped or printed</b> on tags and castings. It needs the tag to fill the frame, square on, sharp, no glare.</p>
          </div>
        </div>
        <p className="text-sm text-slate-700">A photo that is good for one is usually poor for the other. That is why both buttons exist, and why you pick the photos you send.</p>
      </Block>

      <Block icon={ScanText} title="How to shoot a part (the standing rule)" tone="amber" testid="guide-sop">
        <Step n={1}><b>Two overview shots.</b> Whole part in frame, from the front and from one side. Stand back. These are what gets it identified.</Step>
        <Step n={2}><b>One close-up of every tag or stamped number you can see.</b> Tag fills the frame, camera square on, no shadow across it. Wipe it first.</Step>
        <Step n={3}><b>If a number is hard to read with your own eyes, take a second shot at an angle.</b> The AI is reading the same picture you are.</Step>
        <Step n={4}><b>Shoot the bolt-on components' tags too</b> — pump, sensor, solenoid, filter head. They are part of the part you are selling.</Step>
        <Step n={5}><b>Then send the right photos.</b> Identify: the overview shots. Read numbers: the tag close-ups. You tick what goes in — nothing is sent until you do.</Step>
      </Block>

      <Block icon={Tag} title="Bolt-on components — what they are, and why they matter" testid="guide-bolt-ons">
        <p className="text-sm text-slate-700">
          An assembly is the thing you <b>name and sell</b> — an engine, a transmission, a door. Bolted onto
          it are smaller parts that each carry their <b>own tag with their own numbers</b>: a Bosch pump,
          an injector, a sensor, a filter head. Those are <b>bolt-on components</b>.
        </p>
        <p className="text-sm text-slate-700">
          A number off a component's tag is <b>that component's</b> number — not the assembly's. Keep them
          apart and two good things happen: a search for the pump number still finds this engine, and the
          engine's own part number stays the engine's.
        </p>
        <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
          <li>Every number shows <b>which tag it came off</b> and <b>the photo</b> it was read from. Tap the photo to see it full size.</li>
          <li>Every button says <b>where the number is going</b> — "Part number of the assembly" or "Part number of this component".</li>
          <li>Making a component's number the <b>assembly's</b> own number takes two deliberate taps. It is never automatic.</li>
          <li>If a component ever gets pulled off and sold on its own, its numbers and its search words <b>go with it</b>.</li>
        </ul>
      </Block>

      <Block icon={Sparkles} title="What the buttons do" testid="guide-buttons">
        <ul className="text-sm text-slate-700 space-y-1.5">
          <li><b>Identify this part</b> — one charged read. Suggests name, brand, type, category, keywords. Nothing is applied until you click it.</li>
          <li><b>Read numbers off the part</b> — one charged read, numbers only.</li>
          <li><b>Read the numbers again</b> — reads the numbers over, as a fresh charged read. Use it after better photos. It never re-identifies the part.</li>
          <li><b>Re-read everything</b> — only tick this when you want photos that were already read paid for and read again. Left alone, a read that stopped halfway <b>picks up where it stopped</b>.</li>
          <li><b>✓verify</b> — you checked that value against the metal. One value, one tap, every time: there is no bulk tick, on purpose.</li>
          <li><b>Hold any button down on a tablet</b> (or hover it on a PC) to see what it does.</li>
        </ul>
      </Block>

      <Block icon={AlertOctagon} title="The red stop line" tone="red" testid="guide-red">
        <p className="text-sm text-slate-700">
          Red means: <b>we already hold a number one character away from this one</b>, on the same kind of
          part. It is usually a misread — a 0 read as a 2. Tap <b>See them side by side</b>, compare both
          against the stamp in your hand, fix the character if it is wrong, then ✓verify.
        </p>
        <p className="text-sm text-slate-700">
          Red is deliberately rare. A date, a rating or a quantity never raises it, and neither does a
          number held on a different kind of part. The <b>orange "unclear characters"</b> line is different —
          that is the AI telling you which characters it was unsure of, on every read.
        </p>
      </Block>

      <Block icon={Camera} title="If a read stops partway" testid="guide-stopped">
        <p className="text-sm text-slate-700">
          Big reads run in batches on the server. Each batch that finishes is <b>saved and marked done</b>.
          If a read stops, start it again: by default it reads <b>only the photos it never got to</b>, so no
          photo is paid for twice. You can close the panel and come back — the read keeps going.
        </p>
      </Block>

      <p className="text-[11px] text-slate-400 print:hidden">Friedrich Auto &amp; Truck · ask your supervisor if anything here does not match what you see on screen.</p>
    </div>
  );
}
